import type {
	AttendanceDay as APIAttendanceDay,
	AttendanceDayAttachment as APIAttendanceDayAttachment,
	AttendanceDayRecord as APIAttendanceDayRecord,
	AttendanceWorkerDays as APIAttendanceWorkerDays,
	ListPendingAttendanceResponse as APIPendingAttendance,
	TimeEntry as APITimeEntry,
	TimeEntryEdit as APITimeEntryEdit,
	TimeEntryLocation as APITimeEntryLocation,
	TimeEntryWorker as APITimeEntryWorker,
} from "@/adapters/api";
import type {
	AttendanceAttachment,
	AttendanceDay,
	AttendanceRecord,
	AttendanceRecordStatus,
	AttendanceWorkerDays,
	PendingAttendance,
	TimeEntry,
	TimeEntryEdit,
	TimeEntryLocation,
	TimeEntryWorker,
} from "../types";

const RECORD_STATUSES: AttendanceRecordStatus[] = [
	"present",
	"absent",
	"medical_leave",
	"day_off",
];

function toLocation(
	location: APITimeEntryLocation | null | undefined,
): TimeEntryLocation | null {
	if (!location) return null;
	return {
		lat: location.lat,
		lng: location.lng,
		accuracyM: location.accuracy_m ?? null,
		distanceM: location.distance_m ?? null,
	};
}

export function toTimeEntryWorker(worker: APITimeEntryWorker): TimeEntryWorker {
	return {
		id: worker.id,
		name: worker.name,
		jobTitle: worker.job_title ?? "",
		role: worker.role ?? "",
		userId: worker.user_id,
	};
}

function toTimeEntryEdit(
	edited: APITimeEntryEdit | null | undefined,
): TimeEntryEdit | null {
	return edited
		? {
				by: edited.by ?? "",
				at: edited.at,
				note: edited.note ?? "",
			}
		: null;
}

export function toTimeEntry(entry: APITimeEntry): TimeEntry {
	return {
		id: entry.id,
		workDate: entry.work_date,
		clockedInAt: entry.clocked_in_at,
		clockedOutAt: entry.clocked_out_at,
		clockInLocation: toLocation(entry.clock_in_location),
		clockOutLocation: toLocation(entry.clock_out_location),
		totalMinutes: entry.total_minutes,
		method: entry.method ?? "app",
		note: entry.note ?? "",
		worker: toTimeEntryWorker(entry.worker),
		edited: toTimeEntryEdit(entry.edited),
	};
}

export function toAttendanceAttachment(
	attachment: APIAttendanceDayAttachment,
): AttendanceAttachment {
	return {
		id: attachment.id,
		fileName: attachment.file_name,
		contentType: attachment.content_type,
		sizeBytes: attachment.size_bytes,
		createdAt: attachment.created_at,
	};
}

function toRecordStatus(status: string): AttendanceRecordStatus {
	return RECORD_STATUSES.includes(status as AttendanceRecordStatus)
		? (status as AttendanceRecordStatus)
		: "present";
}

export function toAttendanceRecord(
	record: APIAttendanceDayRecord | null,
): AttendanceRecord | null {
	if (!record) return null;
	return {
		id: record.id,
		status: toRecordStatus(record.status),
		note: record.note ?? "",
		overtimeMinutesOverride: record.overtime_minutes_override,
		overtimePctOverride: record.overtime_pct_override,
		edited: toTimeEntryEdit(record.edited),
		attachments: (record.attachments ?? []).map(toAttendanceAttachment),
	};
}

export function toAttendanceDay(day: APIAttendanceDay): AttendanceDay {
	return {
		date: day.date,
		kind: day.kind,
		status: day.status,
		entries: (day.entries ?? []).map(toTimeEntry),
		record: toAttendanceRecord(day.record),
		totals: {
			workedMinutes: day.totals.worked_minutes,
			expectedMinutes: day.totals.expected_minutes,
			overtimeMinutes: day.totals.overtime_minutes,
			missingMinutes: day.totals.missing_minutes,
			overtimePct: day.totals.overtime_pct,
			overtimeEquivalentMinutes: day.totals.overtime_equivalent_minutes,
		},
	};
}

export function toAttendanceWorkerDays(
	item: APIAttendanceWorkerDays,
): AttendanceWorkerDays {
	return {
		worker: toTimeEntryWorker(item.worker),
		days: (item.days ?? []).map(toAttendanceDay),
	};
}

export function toPendingAttendance(
	response: APIPendingAttendance,
): PendingAttendance {
	return {
		from: response.from,
		to: response.to,
		count: response.count,
		workers: (response.workers ?? []).map((item) => ({
			worker: toTimeEntryWorker(item.worker),
			dates: item.dates ?? [],
		})),
	};
}
